import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { RootState } from '../../../stateStore'
import {
  AssetListSliceState,
  AssetTypeChangeList,
  ManufacturerList,
  AllAssetListProps,
  AllAssetsList,
  AssetProps,
  AssetHistoryProps,
} from '../../../types/Assets/AssetList/AssetListTypes'
import AssetListApi from '../../../middleware/api/Assets/AssetList/AssetListApi'

const getAssetTypeChangeList = createAsyncThunk(
  'asset/getAssetList',
  async (id: number, thunkApi) => {
    try {
      return await AssetListApi.getAssetTypeChangeList(id)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllAssetListData = createAsyncThunk(
  'allAsset/getallassetlist',
  async (props: AllAssetListProps, thunkApi) => {
    try {
      return await AssetListApi.getAllAssetListData(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getAllAssetHistoryData = createAsyncThunk(
  'allAsset/getAssetHistory',
  async (props: AssetProps, thunkApi) => {
    try {
      return await AssetListApi.getAssetHistory(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const curMonth = ''
const assetStatus = ''
const asset = ''
const assetType = ''
const pTypeVal = ''
const searchEmpName = false
export const initialAssetTypeChangeListState: AssetListSliceState = {
  asset: [],
  isLoading: ApiLoadingState.idle,
  manufacturerList: {} as ManufacturerList,
  allAssetList: [],
  assetHistoryList: [],
  listSize: 0,
  assetList: {} as AllAssetsList,
  currentPage: 1,
  pageSize: 20,
  selectMonthValue: curMonth,
  assetValue: asset,
  assetTypeValue: assetType,
  ProductTypeValue: pTypeVal,
  assetStatusValue: assetStatus,
  fromDate: '',
  toDate: '',
  searchInput: '',
  searchByEmployeeName: searchEmpName,
  searchInputID: '',
  setSelectProduct: '',
}

const assetTypeChangeListSlice = createSlice({
  name: 'assetTypeChangeList',
  initialState: initialAssetTypeChangeListState,
  reducers: {
    setMonthValue: (state, action) => {
      state.selectMonthValue = action.payload
    },
    clearSelectMonth: (state) => {
      state.selectMonthValue = ''
    },
    setAssetList: (state, action) => {
      state.assetValue = action.payload
    },
    clearAssetList: (state, action) => {
      state.allAssetList = action.payload
    },
    clearAssetListType: (state, action) => {
      state.allAssetList = action.payload
    },
    setAssetStatusValue: (state, action) => {
      state.assetStatusValue = action.payload
    },
    clearStatusValue: (state) => {
      state.assetStatusValue = ''
    },
    setAssetTypeStatus: (state, action) => {
      state.assetTypeValue = action.payload
    },
    clearAssetTypeStatus: (state) => {
      state.assetTypeValue = ''
    },
    setProductTypeStatus: (state, action) => {
      state.ProductTypeValue = action.payload
    },
    cleartProductTypeStatus: (state) => {
      state.ProductTypeValue = ''
    },
    setFromDate: (state, action) => {
      state.fromDate = action.payload
    },
    setToDate: (state, action) => {
      state.toDate = action.payload
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload
    },
    setSearchByEmployee: (state, action) => {
      state.searchByEmployeeName = action.payload
    },
    clearSearchByEmployee: (state, action) => {
      state.searchByEmployeeName = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSearchInputID: (state, action) => {
      state.searchInputID = action.payload
    },
    setSelectedProduct: (state, action) => {
      state.setSelectProduct = action.payload
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getAllAssetHistoryData.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.assetHistoryList = action.payload
      })
      .addCase(getAssetTypeChangeList.fulfilled, (state, action) => {
        state.asset = action.payload
      })
      .addCase(getAllAssetListData.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.allAssetList = action.payload.list
        state.listSize = action.payload.size
      })
      .addMatcher(
        isAnyOf(getAllAssetHistoryData.pending, getAllAssetListData.pending),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const assetListThunk = {
  getAssetTypeChangeList,
  getAllAssetListData,
  getAllAssetHistoryData,
}

const isLoading = (state: RootState): LoadingState => state.assetList.isLoading
const listSize = (state: RootState): number => state.assetList.listSize

const assetListData = (state: RootState): AssetTypeChangeList[] =>
  state.assetList.asset
const manufacturerList = (state: RootState): ManufacturerList =>
  state.assetList.manufacturerList
const allAssetListData = (state: RootState): AllAssetsList[] =>
  state.assetList.allAssetList

const allAssetList = (state: RootState): AllAssetsList =>
  state.assetList.assetList
const assetHistory = (state: RootState): AssetHistoryProps[] =>
  state.assetList.assetHistoryList

const pageFromState = (state: RootState): number => state.assetList.currentPage
const pageSizeFromState = (state: RootState): number => state.assetList.pageSize
// -----------------------------------------------------------

const getSelectedMonthValue = (state: RootState): string =>
  state.assetList.selectMonthValue

const getSelectedAsseStatusValue = (state: RootState): string =>
  state.assetList.assetStatusValue

const getAssetListValue = (state: RootState): string =>
  state.assetList.assetValue

const getSelectedAssetTypeValue = (state: RootState): string =>
  state.assetList.assetTypeValue

const getSelectedProductTypeValue = (state: RootState): string =>
  state.assetList.ProductTypeValue

const getFromDateValue = (state: RootState): string | Date =>
  state.assetList.fromDate

const getToDateValue = (state: RootState): string | Date =>
  state.assetList.toDate

const getSearchInputValue = (state: RootState): string =>
  state.assetList.searchInput
const getSearchInputIDValue = (state: RootState): string =>
  state.assetList.searchInputID
const getSearchByEmpName = (state: RootState): boolean =>
  state.assetList.searchByEmployeeName
const getSelectedProductValue = (state: RootState): string =>
  state.assetList.setSelectProduct

const assetListSelectors = {
  isLoading,
  assetListData,
  manufacturerList,
  listSize,
  allAssetListData,
  allAssetList,
  assetHistory,
  pageFromState,
  pageSizeFromState,

  getSelectedMonthValue,
  getSelectedAsseStatusValue,
  getSelectedAssetTypeValue,
  getSelectedProductTypeValue,
  getFromDateValue,
  getToDateValue,
  getSearchInputValue,
  getSearchByEmpName,
  getAssetListValue,
  getSearchInputIDValue,
  getSelectedProductValue,
}

export const assetListService = {
  ...assetListThunk,
  actions: assetTypeChangeListSlice.actions,
  selectors: assetListSelectors,
}

export default assetTypeChangeListSlice.reducer
